import { useEffect, useId, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, Form, Input, Label, Select, TextArea } from '@shared/components';
import {
    buildCancelAgreementSchema,
    CANCEL_NOTE_MAX_LENGTH,
    type CancelAgreementFormInput,
    type CancelAgreementValues,
} from '../../../schemas/cancelAgreement.schema';
import { REASON_LABEL, REQUESTING_PARTY_LABEL, SCRIPT_POINTS } from '../../../utils/cancellationCopy';
import { CANCELLATION_REASONS, REQUESTING_PARTIES, type CancelAction } from '../../../types/cancellation.types';
import { CancelAgreementSummary } from './CancelAgreementSummary';
import { CancellationEstimate } from './CancellationEstimate';
import { ServiceHistoryFields } from './ServiceHistoryFields';

export type CancelAgreementStep = 'details' | 'confirm';

type TextField = Exclude<keyof CancelAgreementFormInput, 'serviceHistoryConfirmed'>;

interface Props {
    agreementRef: string;
    action: Pick<CancelAction, 'estimate' | 'estimateUnavailable' | 'requiresServiceHistory'>;
    initialValues?: Partial<CancelAgreementFormInput>;
    step: CancelAgreementStep;
    pending: boolean;
    serverError: string | null;
    onContinue: () => void;
    onBack: () => void;
    onSubmit: (values: CancelAgreementValues) => void;
}

const REASON_OPTIONS = CANCELLATION_REASONS.map((value) => ({ value, label: REASON_LABEL[value] }));
const PARTY_OPTIONS = REQUESTING_PARTIES.map((value) => ({ value, label: REQUESTING_PARTY_LABEL[value] }));

const EMPTY_FORM: CancelAgreementFormInput = {
    reason: '',
    note: '',
    requestingParty: '',
    caseReference: '',
    serviceHistoryConfirmed: false,
    serviceHistorySource: '',
    serviceHistoryDetail: '',
};

export function CancelAgreementPanel(props: Props) {
    const headingId = useId();
    const headingRef = useRef<HTMLSpanElement>(null);
    const firstRender = useRef(true);
    const { step, agreementRef } = props;

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        headingRef.current?.focus();
    }, [step]);

    return (
        <Card ariaLabelledBy={headingId}>
            <CardHeader>
                <CardTitle as="h3" id={headingId}>
                    <span ref={headingRef} tabIndex={-1} className="outline-none">
                        {step === 'details' ? `Cancel agreement ${agreementRef}` : 'Confirm cancellation'}
                    </span>
                </CardTitle>
                <p className="mt-1 text-sm text-mp-muted">
                    {step === 'details'
                        ? 'Tell the customer the following before you start.'
                        : `Check these details before you cancel agreement ${agreementRef}.`}
                </p>
            </CardHeader>
            <CardContent>
                <CancelAgreementForm {...props} />
            </CardContent>
        </Card>
    );
}

function CancelAgreementForm({ action, initialValues, step, pending, serverError, onContinue, onBack, onSubmit }: Props) {
    const ids = { reason: useId(), party: useId(), note: useId(), caseRef: useId() };
    const schema = useMemo(() => buildCancelAgreementSchema(action.requiresServiceHistory), [action.requiresServiceHistory]);
    const {
        watch,
        setValue,
        handleSubmit,
        formState: { errors },
    } = useForm<CancelAgreementFormInput, unknown, CancelAgreementValues>({
        resolver: zodResolver(schema),
        mode: 'onBlur',
        defaultValues: { ...EMPTY_FORM, ...initialValues },
        shouldFocusError: false,
    });
    const values = watch();
    const setField = (name: TextField, value: string) => setValue(name, value, { shouldValidate: !!errors[name] });
    const blocked = action.estimate === null || action.estimateUnavailable;

    return (
        <Form
            onSubmit={handleSubmit((parsed) => (step === 'details' ? onContinue() : onSubmit(parsed)))}
            noValidate
            className="space-y-4"
        >
            {step === 'details' ? (
                <>
                    <ul className="list-disc space-y-1 pl-5 text-sm text-mp-foreground">
                        {SCRIPT_POINTS.map((point) => (
                            <li key={point}>{point}</li>
                        ))}
                    </ul>
                    <CancellationEstimate action={action} />
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                            <Label htmlFor={ids.reason} required>
                                Reason for cancelling
                            </Label>
                            <Select
                                id={ids.reason}
                                options={REASON_OPTIONS}
                                placeholder="Select a reason"
                                value={values.reason}
                                onChange={(value) => setField('reason', value)}
                                disabled={pending}
                                error={!!errors.reason}
                                hint={errors.reason?.message}
                                ariaLabel="Reason for cancelling"
                            />
                        </div>
                        <div>
                            <Label htmlFor={ids.party} required>
                                Who asked to cancel
                            </Label>
                            <Select
                                id={ids.party}
                                options={PARTY_OPTIONS}
                                placeholder="Select who asked"
                                value={values.requestingParty}
                                onChange={(value) => setField('requestingParty', value)}
                                disabled={pending}
                                error={!!errors.requestingParty}
                                hint={errors.requestingParty?.message}
                                ariaLabel="Who asked to cancel"
                            />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor={ids.caseRef}>Case reference (optional)</Label>
                        <Input
                            id={ids.caseRef}
                            value={values.caseReference}
                            onChange={(event) => setField('caseReference', event.target.value)}
                            disabled={pending}
                            error={!!errors.caseReference}
                            hint={errors.caseReference?.message}
                            hintId={`${ids.caseRef}-error`}
                        />
                    </div>
                    <div>
                        <Label htmlFor={ids.note} required={values.reason === 'other'}>
                            Note
                        </Label>
                        <TextArea
                            id={ids.note}
                            rows={3}
                            placeholder="Anything the next person should know"
                            value={values.note}
                            onChange={(value) => setField('note', value)}
                            disabled={pending}
                            error={!!errors.note}
                            hint={errors.note?.message}
                            hintId={`${ids.note}-error`}
                        />
                        <p className="mt-1 text-right text-xs text-mp-muted" aria-live="polite">
                            {values.note.length} / {CANCEL_NOTE_MAX_LENGTH}
                        </p>
                    </div>
                    {action.requiresServiceHistory ? (
                        <ServiceHistoryFields
                            confirmed={values.serviceHistoryConfirmed}
                            source={values.serviceHistorySource}
                            detail={values.serviceHistoryDetail}
                            disabled={pending}
                            errors={{
                                confirmed: errors.serviceHistoryConfirmed?.message,
                                source: errors.serviceHistorySource?.message,
                                detail: errors.serviceHistoryDetail?.message,
                            }}
                            onConfirmedChange={(checked) =>
                                setValue('serviceHistoryConfirmed', checked, { shouldValidate: !!errors.serviceHistoryConfirmed })
                            }
                            onSourceChange={(source) => setField('serviceHistorySource', source)}
                            onDetailChange={(detail) => setField('serviceHistoryDetail', detail)}
                        />
                    ) : (
                        <p className="text-xs text-mp-muted">
                            By continuing you confirm that all servicing carried out has been invoiced.
                        </p>
                    )}
                </>
            ) : (
                <CancelAgreementSummary values={values} action={action} />
            )}
            {serverError && <Alert variant="error">{serverError}</Alert>}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                {step === 'confirm' && (
                    <Button type="button" variant="neutral" appearance="ghost" disabled={pending} onClick={onBack} className="w-full sm:w-auto">
                        Back
                    </Button>
                )}
                {step === 'details' ? (
                    <Button type="submit" variant="primary" appearance="solid" disabled={pending || blocked} className="w-full sm:w-auto">
                        Continue
                    </Button>
                ) : (
                    <Button type="submit" variant="danger" appearance="solid" disabled={pending} className="w-full sm:w-auto">
                        {pending ? 'Starting...' : 'Cancel agreement'}
                    </Button>
                )}
            </div>
        </Form>
    );
}
